import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import styles from './LandingPage.module.css';
import pageStyles from './ContactPage.module.css';
import { contactAPI } from '../api/contact';
import ChatWidget from './ChatWidget';
import SiteNav from './SiteNav';
import SiteFooter from './SiteFooter';

// محتوای این صفحه بخشی از همان AppConfig مربوط به صفحه اصلی (`landing_page`) است
// (کلیدهای contactTitle/contactDesc/contactLinks/... را از پنل مدیریت محتوای
// صفحه اصلی ویرایش می‌کنید) — این‌جا فقط جدا از بقیه‌ی صفحه اصلی، با همان
// ظاهر جادویی (استارفیلد، ذرات شناور، پس‌زمینه تیره طلایی) رندر می‌شود.
const T = {
  fa: {
    dir: 'rtl',
    badgeLabel: '🦉 ارسال پیام به هاگوارتز',
    pageTitle: 'با ما در ارتباط باش',
    pageSubtitle: 'سوال، پیشنهاد یا مشکلی داری؟ فرم زیر را پر کن تا پیامت مستقیم به جغدهای وزارت سحر و جادو و مدیران جامعه برسد؛ یا از طریق کانال‌های زیر همراه ما شو.',
    contactTitle: 'ارتباط با ما',
    contactDesc: 'اگر سوال، پیشنهاد یا مشکلی دارید، فرم زیر را پر کنید تا مستقیماً به وزارت سحر و جادو و مدیران جامعه ارسال شود. همچنین می‌توانید از طریق دکمه‌های زیر به کانال تلگرام یا انجمن پاترهدها بپیوندید.',
    contactFormName: 'نام شما',
    contactFormEmail: 'ایمیل یا شماره تماس',
    contactFormSubject: 'موضوع پیام (اختیاری)',
    contactFormMessage: 'متن پیام شما...',
    contactSubmit: '🪄 ارسال پیام به هاگوارتز',
    contactSuccess: '✓ پیام شما با موفقیت به جغدها سپرده شد!',
    contactLinks: [
      { icon: '✈️', image: '', imageWidth: '', imageHeight: '', label: 'کانال تلگرام ما', url: 'https://t.me/WwWPotterHeadIR' },
      { icon: '👥', image: '', imageWidth: '', imageHeight: '', label: 'انجمن گفتگو', url: '/forum' }
    ],
  },
  en: {
    dir: 'ltr',
    badgeLabel: '🦉 Send an Owl to Hogwarts',
    pageTitle: 'Get in Touch',
    pageSubtitle: 'Have a question, suggestion, or issue? Fill out the form below to send a message straight to our Hogwarts governors, or connect through the channels below.',
    contactTitle: 'Contact Us',
    contactDesc: 'If you have any questions, feedback, or issues, please fill out the form below to send a message straight to our Hogwarts governors. You can also join our global Telegram community or discussion forum below.',
    contactFormName: 'Your Name',
    contactFormEmail: 'Email or Phone Number',
    contactFormSubject: 'Subject (Optional)',
    contactFormMessage: 'Your message...',
    contactSubmit: '🪄 Send Owl Message',
    contactSuccess: '✓ Your message has been sent to the owls!',
    contactLinks: [
      { icon: '✈️', image: '', imageWidth: '', imageHeight: '', label: 'Telegram Channel', url: 'https://t.me/potterheads_org' },
      { icon: '👥', image: '', imageWidth: '', imageHeight: '', label: 'Discussion Forum', url: '/forum' }
    ],
  }
};

// ── Stars background (همان جلوه‌ی صفحه اصلی) ───────────────────────────────
function StarField() {
  const stars = Array.from({ length: 120 }, (_, i) => ({
    id: i,
    top: Math.random() * 100,
    left: Math.random() * 100,
    size: Math.random() * 2.5 + 0.5,
    delay: Math.random() * 4,
    duration: Math.random() * 3 + 2,
  }));
  return (
    <div className={styles.starField} aria-hidden>
      {stars.map(s => (
        <span
          key={s.id}
          className={styles.star}
          style={{
            top: `${s.top}%`,
            left: `${s.left}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            animationDelay: `${s.delay}s`,
            animationDuration: `${s.duration}s`,
          }}
        />
      ))}
    </div>
  );
}

// ── Floating particles (همان جلوه‌ی صفحه اصلی) ─────────────────────────────
function Particles() {
  const items = ['⚡', '🌟', '✨', '🪄', '⭐', '💫', '🔮', '🦉'];
  return (
    <div className={styles.particles} aria-hidden>
      {Array.from({ length: 14 }, (_, i) => (
        <span
          key={i}
          className={styles.particle}
          style={{
            left: `${(i * 7.3) % 100}%`,
            animationDelay: `${(i * 0.7) % 6}s`,
            animationDuration: `${6 + (i % 4)}s`,
            fontSize: `${1 + (i % 3) * 0.4}rem`,
          }}
        >
          {items[i % items.length]}
        </span>
      ))}
    </div>
  );
}

// ── تشخیص حالت موبایل (برای انتخاب سایز جداگانه‌ی تصاویر روی موبایل) ────────
function useIsMobile(breakpoint = 700) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener ? mq.addEventListener('change', onChange) : mq.addListener(onChange);
    return () => {
      mq.removeEventListener ? mq.removeEventListener('change', onChange) : mq.removeListener(onChange);
    };
  }, [breakpoint]);
  return isMobile;
}

function imageSizeStyle(item, isMobile, { noMaxWidth = false } = {}) {
  if (!item) return {};
  const w = (isMobile && item.imageWidthMobile) || item.imageWidth;
  const h = (isMobile && item.imageHeightMobile) || item.imageHeight;
  return {
    ...(w ? { width: `${w}px`, ...(noMaxWidth ? { maxWidth: 'none' } : {}) } : {}),
    ...(h ? { height: `${h}px` } : {}),
  };
}

function textSizeVars(sizes, role) {
  const s = sizes?.[role];
  if (!s) return undefined;
  const vars = {};
  if (s.desktop) vars[`--ts-${role}-d`] = `${s.desktop}px`;
  if (s.mobile) vars[`--ts-${role}-m`] = `${s.mobile}px`;
  return Object.keys(vars).length ? vars : undefined;
}

function ContactSection({ content, textSizes, isMobile }) {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'fa';
  const IS_PERSIAN = lang === 'fa';

  const [name, setName] = useState('');
  const [emailOrPhone, setEmailOrPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!name.trim()) {
      setError(IS_PERSIAN ? 'نام خود را وارد کنید' : 'Please enter your name');
      return;
    }
    if (!emailOrPhone.trim()) {
      setError(IS_PERSIAN ? 'ایمیل یا شماره تماس را وارد کنید' : 'Please enter your email or phone number');
      return;
    }
    if (!messageText.trim()) {
      setError(IS_PERSIAN ? 'متن پیام خود را وارد کنید' : 'Please enter your message');
      return;
    }

    setLoading(true);
    try {
      const res = await contactAPI.submitMessage({
        name: name.trim(),
        emailOrPhone: emailOrPhone.trim(),
        subject: subject.trim(),
        message: messageText.trim(),
        lang
      });
      if (res.data?.success) {
        setSuccess(true);
        setName('');
        setEmailOrPhone('');
        setSubject('');
        setMessageText('');
      } else {
        setError(res.data?.message || (IS_PERSIAN ? 'خطا در ثبت پیام' : 'Failed to submit message'));
      }
    } catch (err) {
      setError(err.response?.data?.message || (IS_PERSIAN ? 'خطا در اتصال به سرور' : 'Failed to connect to server'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className={styles.contactSection} id="contact">
      <div className={styles.container}>
        <div className={styles.contactCard}>
          <div className={styles.contactIntro}>
            <h2 style={textSizeVars(textSizes, 'contactTitle')}>{content.contactTitle}</h2>
            <p style={textSizeVars(textSizes, 'contactDesc')}>{content.contactDesc}</p>
            <div className={styles.contactLinks}>
              {(content.contactLinks || []).map((item, i) => {
                const isExternal = /^https?:\/\//i.test(item.url || '');
                return (
                  <a
                    key={i}
                    href={item.url || '#'}
                    target={isExternal ? '_blank' : undefined}
                    rel={isExternal ? 'noreferrer' : undefined}
                    className={styles.contactLinkBtn}
                  >
                    {item.image ? (
                      <img
                        src={item.image}
                        alt=""
                        className={styles.contactLinkImg}
                        style={imageSizeStyle(item, isMobile)}
                      />
                    ) : item.icon ? (
                      <span>{item.icon}</span>
                    ) : null}
                    {item.label}
                  </a>
                );
              })}
            </div>
          </div>
          <form className={styles.contactForm} onSubmit={handleSubmit}>
            <div className={styles.contactFormRow}>
              <input
                type="text"
                className={styles.inputField}
                placeholder={content.contactFormName}
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={loading}
              />
              <input
                type="text"
                className={styles.inputField}
                placeholder={content.contactFormEmail}
                value={emailOrPhone}
                onChange={e => setEmailOrPhone(e.target.value)}
                disabled={loading}
              />
              <input
                type="text"
                className={styles.inputField}
                placeholder={content.contactFormSubject}
                value={subject}
                onChange={e => setSubject(e.target.value)}
                disabled={loading}
              />
            </div>
            <textarea
              className={`${styles.inputField} ${styles.textareaField}`}
              placeholder={content.contactFormMessage}
              value={messageText}
              onChange={e => setMessageText(e.target.value)}
              disabled={loading}
            />
            <button type="submit" className={styles.contactSubmitBtn} disabled={loading}>
              {loading ? (IS_PERSIAN ? 'در حال ارسال...' : 'Sending...') : content.contactSubmit}
            </button>
            {success && <div className={styles.successText}>{content.contactSuccess}</div>}
            {error && <div className={styles.errorText}>⚠️ {error}</div>}
          </form>
        </div>
      </div>
    </section>
  );
}

export default function ContactPage() {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'fa';
  const t = T[lang] || T.en;

  const [navScrolled, setNavScrolled] = useState(false);
  // مثل LandingPage.jsx: محتوای این بخش هم از همان AppConfig صفحه اصلی
  // (`landing_page`) خوانده می‌شود تا ویرایش‌های پنل ادمین این‌جا هم اعمال شوند.
  const [content, setContent] = useState(t);
  const [textSizes, setTextSizes] = useState({});
  const isMobile = useIsMobile();

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setContent(t);
    let mounted = true;
    axios.get(`/api/landing?_=${Date.now()}`)
      .then((res) => {
        const fetched = res.data?.content?.[lang];
        if (mounted && fetched) {
          setContent((prev) => ({ ...prev, ...fetched }));
        }
        if (mounted && res.data?.content?.textSizes) {
          setTextSizes(res.data.content.textSizes);
        }
      })
      .catch(() => {}); // در صورت خطا، مقادیر پیش‌فرض هاردکد باقی می‌مانند
    return () => { mounted = false; };
  }, [lang]);

  return (
    <div className={styles.page} dir={t.dir}>
      <StarField />
      <Particles />

      {/* ── NAV ── */}
      <SiteNav fixed scrolled={navScrolled} />

      {/* ── PAGE HEADER (همان رنگ/فونت جادویی صفحه اصلی، اما فاصله‌های مخصوص و
          فشرده‌تر این صفحه در ContactPage.module.css تا در موبایل فضای خالی
          کمتری زیر ناوبری ثابت باقی بماند) ── */}
      <section className={pageStyles.header}>
        <div className={pageStyles.headerInner}>
          <span className={pageStyles.badge}>{t.badgeLabel}</span>
          <h1 className={pageStyles.title}>{t.pageTitle}</h1>
          <p className={pageStyles.subtitle}>{t.pageSubtitle}</p>
        </div>
      </section>

      {/* ── CONTACT ── */}
      <ContactSection content={content} textSizes={textSizes} isMobile={isMobile} />

      {/* ── FOOTER ── */}
      <SiteFooter />

      <ChatWidget />
    </div>
  );
}
